import React, { useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

interface CollapsibleContentProps {
  children: ReactNode;
  header?: ReactNode;
  defaultExpanded?: boolean;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  expandButtonPosition?: 'left' | 'right';
  expandButtonClassName?: string;
  onToggle?: (expanded: boolean) => void;
}

const CollapsibleContent: React.FC<CollapsibleContentProps> = ({
  children,
  header,
  defaultExpanded = true,
  className = '',
  headerClassName = '',
  contentClassName = '',
  expandButtonPosition = 'right',
  expandButtonClassName = '',
  onToggle,
}) => {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

  const toggleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onToggle?.(newExpanded);
  };

  const expandButton = (
    <button
      className={twMerge(
        `flex items-end justify-end transition-transform duration-300 text-secondary focus:outline-none`,
        expandButtonClassName
      )}
      aria-label={
        expanded
          ? t('collapse', { defaultValue: 'Réduire' })
          : t('expand', { defaultValue: 'Déplier' })
      }
      onClick={e => {
        e.stopPropagation();
        toggleExpand();
      }}
    >
      <svg
        className={`w-6 h-6 md:w-5 md:h-5 transform ${expanded ? 'rotate-180' : 'rotate-0'}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  return (
    <div className={className}>
      {header && (
        <div
          className={twMerge(
            `flex items-center md:gap-4 ${expandButtonPosition === 'left' ? 'flex-row' : 'flex-row-reverse'}`,
            headerClassName
          )}
        >
          {expandButton}
          <div className="flex-1 cursor-pointer select-none" onClick={toggleExpand}>
            {header}
          </div>
        </div>
      )}
      {!header && expandButtonPosition === 'right' && (
        <div className="flex justify-end">{expandButton}</div>
      )}
      <div
        className={twMerge(
          `overflow-hidden transition-all duration-500 ${
            expanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
          }`,
          contentClassName
        )}
        style={{ willChange: 'max-height, opacity' }}
      >
        {children}
      </div>
      {!header && expandButtonPosition === 'left' && (
        <div className="flex justify-start">{expandButton}</div>
      )}
    </div>
  );
};

export default CollapsibleContent;
